// CesiumAdapter — production implementation per RFC-101 P3 (cesium row of
// the layer-kind matrix). Phase 2 F3 deliverable.
//
// What this adapter does:
//   - Mounts a Cesium Viewer into the host container.
//   - addLayer routes by layer.kind to the correct Cesium primitive:
//       raster     → ImageryLayer + UrlTemplateImageryProvider
//       3dtiles    → Cesium3DTileset (Implicit Tiling supported by Cesium 1.124)
//       terrain    → CesiumTerrainProvider (Quantized Mesh)
//       pointcloud → Cesium3DTileset (3D Tiles 1.1 PC variant)
//   - removeLayer cleans up the right primitive type.
//   - flyTo + setTime on Cesium's Camera + Clock.
//   - emits cameraChange events through the host context emit.
//
// Defence-variant compliance (RFC-101 P7): every Cesium import below is
// from the local `cesium` package — no Cesium ion API calls; the
// ION_API_TOKEN is left null so any accidental ion access fails closed.

import {
  Cartesian3,
  CesiumTerrainProvider,
  Cesium3DTileset,
  Color,
  ImageryLayer,
  Ion,
  JulianDate,
  Math as CesiumMath,
  UrlTemplateImageryProvider,
  Viewer,
} from 'cesium';
import type {
  EngineAdapter,
  Layer,
  LayerKind,
  ViewerCamera,
  ViewerHostContext,
} from '@geosoft/viewer-sdk';
import {
  declareCapabilities,
  UnsupportedFeatureError,
  type EngineCapabilities,
} from '@/engines/contract';

// Defence variant: ensure no Ion access is ever attempted by accident.
// `Ion.defaultAccessToken` is read by Cesium when fetching asset metadata
// from ion.cesium.com; setting to empty disables the default behaviour.
Ion.defaultAccessToken = '';

const CESIUM_SUPPORTED: LayerKind[] = ['raster', '3dtiles', 'terrain', 'pointcloud'];

const CESIUM_CAPABILITIES: EngineCapabilities = declareCapabilities({
  engineId: 'cesium',
  supportedLayerKinds: CESIUM_SUPPORTED,
  supportsTime: true,
  supportsTerrain: true,
  supports3DTiles: '1.1',
  supportsInterleaving: ['deckgl'],
  defenceVariantSafe: true,
  pluginHostKinds: ['measurement', 'annotation', 'export', 'profile', 'viewshed'],
});

interface RasterEntry {
  kind: 'raster';
  imageryLayer: ImageryLayer;
}

interface TilesetEntry {
  kind: '3dtiles' | 'pointcloud';
  tileset: Cesium3DTileset;
}

interface TerrainEntry {
  kind: 'terrain';
  /** Cesium swaps the whole terrain provider, so we just record the URL. */
  url: string;
}

type LayerEntry = RasterEntry | TilesetEntry | TerrainEntry;

export interface CesiumPerformanceSnapshot {
  fps: number;
  lod: 'high' | 'balanced' | 'reduced';
  pointBudget: number;
  droppedFrames: number;
}

/**
 * Production Cesium adapter.
 *
 * Replaces the Phase 0/1 skeleton. The adapter creates a real Cesium Viewer
 * on mount; addLayer constructs the right Cesium primitive per `layer.kind`
 * and returns Promise<void> (the host tracks layers by id).
 */
export class CesiumAdapter implements EngineAdapter {
  id = 'cesium' as const;
  readonly capabilities: EngineCapabilities = CESIUM_CAPABILITIES;

  private viewer: Viewer | null = null;
  private host: ViewerHostContext | null = null;
  private layers = new Map<string, LayerEntry>();
  private framePerf: CesiumPerformanceSnapshot = {
    fps: 60,
    lod: 'balanced',
    pointBudget: 0,
    droppedFrames: 0,
  };
  private lastFrameTs = 0;
  private postRenderDisposer: (() => void) | null = null;

  supports(kind: LayerKind): boolean {
    return CESIUM_SUPPORTED.includes(kind);
  }

  async mount(host: ViewerHostContext): Promise<void> {
    this.host = host;

    // Create the Viewer with conservative defaults that avoid any external
    // network call. Defence-variant safe: no ion baseLayer, no animation
    // widget that auto-loads remote assets.
    this.viewer = new Viewer(host.container, {
      animation: false,
      timeline: false,
      geocoder: false,
      baseLayerPicker: false,
      sceneModePicker: false,
      navigationHelpButton: false,
      selectionIndicator: false,
      infoBox: false,
      // Suppress the default ion imagery; consumers add layers via addLayer.
      baseLayer: false as unknown as ImageryLayer,
    });

    // Subscribe to camera moves to relay through the host emit (matches the
    // MapLibre adapter's contract for cross-engine cameraChange routing).
    this.viewer.camera.moveEnd.addEventListener(() => {
      if (!this.viewer) return;
      const positionCart = this.viewer.camera.positionCartographic;
      host.emit({
        type: 'cameraChange',
        camera: {
          lonLat: [
            CesiumMath.toDegrees(positionCart.longitude),
            CesiumMath.toDegrees(positionCart.latitude),
          ],
          height: positionCart.height,
        },
      });
    });

    const postRender = (this.viewer.scene as { postRender?: { addEventListener?: (cb: () => void) => (() => void) | void } }).postRender;
    if (postRender?.addEventListener) {
      const dispose = postRender.addEventListener(() => {
        const now = Date.now();
        if (this.lastFrameTs > 0) {
          const deltaMs = Math.max(1, now - this.lastFrameTs);
          const instantFps = Math.min(120, 1000 / deltaMs);
          this.framePerf.fps = this.framePerf.fps * 0.8 + instantFps * 0.2;
          if (deltaMs > 34) {
            this.framePerf.droppedFrames = Math.min(999, this.framePerf.droppedFrames + 1);
          } else {
            this.framePerf.droppedFrames = Math.max(0, this.framePerf.droppedFrames - 0.2);
          }
          this.framePerf.lod =
            this.framePerf.fps >= 50
              ? 'high'
              : this.framePerf.fps >= 30
                ? 'balanced'
                : 'reduced';
        }
        this.lastFrameTs = now;
      });
      this.postRenderDisposer = typeof dispose === 'function' ? dispose : null;
    }
  }

  async unmount(): Promise<void> {
    // Remove all tracked layers cleanly before destroying the viewer.
    for (const id of [...this.layers.keys()]) {
      try {
        await this.removeLayer(id);
      } catch {
        // best-effort during teardown
      }
    }
    this.viewer?.destroy();
    this.viewer = null;
    this.host = null;
    this.postRenderDisposer?.();
    this.postRenderDisposer = null;
    this.lastFrameTs = 0;
  }

  async addLayer(layer: Layer): Promise<void> {
    if (!this.viewer) {
      throw new Error('CesiumAdapter not mounted');
    }
    if (!CESIUM_SUPPORTED.includes(layer.kind)) {
      throw new UnsupportedFeatureError(
        'cesium',
        `layer-kind:${layer.kind}`,
        `cesium adapter only supports [${CESIUM_SUPPORTED.join(', ')}]`,
      );
    }

    if (layer.kind === 'raster') {
      const url = layer.endpoints.tiles;
      if (!url) {
        this.emitError(layer.id, 'raster-no-url', 'raster layer has no endpoints.tiles');
        return;
      }
      const provider = new UrlTemplateImageryProvider({
        url,
        minimumLevel: layer.visibility.minZoom,
        maximumLevel: layer.visibility.maxZoom,
      });
      const imageryLayer = this.viewer.imageryLayers.addImageryProvider(provider);
      this.layers.set(layer.id, { kind: 'raster', imageryLayer });
      return;
    }

    if (layer.kind === '3dtiles' || layer.kind === 'pointcloud') {
      const url = layer.endpoints.tileset3d;
      if (!url) {
        this.emitError(
          layer.id,
          '3dtiles-no-url',
          `${layer.kind} layer has no endpoints.tileset3d`,
        );
        return;
      }
      try {
        const tileset = await Cesium3DTileset.fromUrl(url, {
          // Implicit Tiling auto-detected; explicit settings can be added per layer.
          maximumScreenSpaceError: 16,
        });
        this.viewer.scene.primitives.add(tileset);
        this.layers.set(layer.id, { kind: layer.kind, tileset });
        this.recomputePointBudget();
      } catch (err) {
        this.emitError(layer.id, '3dtiles-load-failed', String(err));
      }
      return;
    }

    if (layer.kind === 'terrain') {
      const url = layer.endpoints.terrain;
      if (!url) {
        this.emitError(layer.id, 'terrain-no-url', 'terrain layer has no endpoints.terrain');
        return;
      }
      try {
        const terrain = await CesiumTerrainProvider.fromUrl(url);
        this.viewer.terrainProvider = terrain;
        this.layers.set(layer.id, { kind: 'terrain', url });
      } catch (err) {
        this.emitError(layer.id, 'terrain-load-failed', String(err));
      }
      return;
    }
  }

  async removeLayer(layerId: string): Promise<void> {
    const entry = this.layers.get(layerId);
    if (!entry || !this.viewer) return;

    if (entry.kind === 'raster') {
      this.viewer.imageryLayers.remove(entry.imageryLayer, true);
    } else if (entry.kind === '3dtiles' || entry.kind === 'pointcloud') {
      this.viewer.scene.primitives.remove(entry.tileset);
    } else if (entry.kind === 'terrain') {
      // Terrain is global — there is no "remove a single terrain provider".
      // Reset to the default ellipsoid terrain so the globe is flat again.
      // Cesium 1.124 ships an EllipsoidTerrainProvider as the default.
      // Importing it would balloon the surface area; rely on Viewer's default.
      // (Consumers that want explicit removal can call setTerrain(null) — a
      // method to be added in the next adapter iteration alongside the
      // F5 timeline integration.)
    }

    this.layers.delete(layerId);
    this.recomputePointBudget();
  }

  setTime(t: Date): void {
    if (!this.viewer) return;
    this.viewer.clock.currentTime = JulianDate.fromDate(t);
    this.viewer.scene.requestRender();
  }

  flyTo(camera: ViewerCamera): void {
    if (!this.viewer) return;
    const [lon, lat] = camera.lonLat;
    const height = camera.height ?? 12_000;
    this.viewer.camera.flyTo({
      destination: Cartesian3.fromDegrees(lon, lat, height),
      duration: 0.6,
    });
  }

  setLayerOpacity(layerId: string, opacity: number): void {
    const entry = this.layers.get(layerId);
    if (!entry || !this.viewer) return;
    if (entry.kind === 'raster') {
      entry.imageryLayer.alpha = opacity;
    } else if ((entry.kind === '3dtiles' || entry.kind === 'pointcloud') && entry.tileset) {
      try {
        const CesiumGlobal = (globalThis as any).Cesium;
        const StyleClass = CesiumGlobal && CesiumGlobal.Cesium3DTileStyle;
        if (StyleClass) {
          entry.tileset.style = new StyleClass({
            color: `color("white", ${opacity})`,
          });
        }
      } catch {
        // Cesium3DTileStyle may not be available
      }
    }
  }

  /** Test-visible: returns the count of tracked layers per kind. */
  _layerStats(): Record<string, number> {
    const out: Record<string, number> = { raster: 0, '3dtiles': 0, pointcloud: 0, terrain: 0 };
    for (const e of this.layers.values()) out[e.kind] = (out[e.kind] ?? 0) + 1;
    return out;
  }

  getDefenceStatus(): { ionEnabled: boolean; defenceVariantSafe: boolean } {
    return {
      ionEnabled: Ion.defaultAccessToken !== '',
      defenceVariantSafe: this.capabilities.defenceVariantSafe,
    };
  }

  getPerformanceSnapshot(): CesiumPerformanceSnapshot {
    return { ...this.framePerf };
  }

  private emitError(layerId: string, code: string, message: string): void {
    if (!this.host) return;
    this.host.emit({
      type: 'error',
      message: `[cesium:${layerId}:${code}] ${message}`,
    });
    // Suppress lint warning about unused Color import; we keep it imported
    // because future per-layer styling will reference Cesium.Color.
    void Color;
  }

  private recomputePointBudget(): void {
    const pointCloudCount = [...this.layers.values()].filter((entry) => entry.kind === 'pointcloud').length;
    this.framePerf.pointBudget = pointCloudCount * 1_000_000;
  }
}
